import { createContext, useContext } from 'react';
import { OnPluginClickEvent, OnSweepNodesChange } from '../SweepCanvas';
import {
  CanvasNode,
  CanvasEdge,
  CanvasGroup,
  OnNodeClickProps,
  CanvasMode,
  CanvasStandaloneNode,
  CanvasSection,
  CanvasStickyNote,
  CanvasTextNode,
} from '../canvas-types';
import { CanvasNodesMouseMove } from '../useCanvasNodesMouseMove';
import { SweepCanvasBackgroundProps } from '../canvas-background/CanvasBackground';
import { SweepCanvasControlsProps } from '../canvas-controls/CanvasControls';
import { VisibilityMap } from '../../../types/VisibilityTypes';
import { DEFAULT_VISIBILITY_MAP } from '../../../constants/visibility';

export type OnConnectClick = (
  event: React.MouseEvent<HTMLButtonElement>,
  node: {
    stepId?: string;
    groupId: string;
  },
  parentEl: HTMLElement | null,
) => any;

export type OnEdgeDeleteClick = (params: {
  edgeId: string;
  sourceNodeId: string;
  targetNodeId: string;
  sourceNodeParentId?: string;
  targetNodeParentId?: string;
}) => any;

export type OnSectionDeleteClick = (sectionId: string) => any;

interface BaseSweepCanvasProps<GroupMetadata = any> {
  sweepNodes: CanvasNode[];
  sweepEdges: CanvasEdge[];
  sweepGroups: CanvasGroup<GroupMetadata>[];
  sweepStandaloneNodes: CanvasStandaloneNode[];
  sweepSections: CanvasSection[];
  sweepStickyNotesNodes: CanvasStickyNote[];
  sweepTextNodes: CanvasTextNode[];
  onSweepNodesChange?: OnSweepNodesChange;
  onNodeClick?: (props: OnNodeClickProps) => any;
  onPillClick?: (props: OnNodeClickProps) => any;
  onLabelClick?: (props: OnNodeClickProps) => any;
  onGateClick?: (sourceNodeId: string, targetNodeId: string, sourceNodeParentId: string) => any;
  onEdgeDeleteClick?: OnEdgeDeleteClick;
  onConnectClick?: OnConnectClick;
  readonly?: boolean;
  fitViewTimeoutValue?: number;
  onNodeNameChange?: (name: string, nodeId: string) => { isValid: boolean; error?: string };
  canvasNodesMouseMove?: CanvasNodesMouseMove;
  holdNodeHighlighted?: boolean;
  hideGroupInfo?: boolean;
  disableGroups?: boolean;
  autoFitViewOnFirstNodes?: boolean;
  hideControls?: boolean;
  isLoadingCursor?: boolean;
  showGroupOverlays?: boolean;
  onPluginClick?: OnPluginClickEvent;
  canvasMode?: CanvasMode;
  disableNodeHighlight?: boolean;
  simpleHighlightedEntities?: string[];
  visibilityMap: VisibilityMap;
  disableMultiSelection?: boolean;
  onlyRenderVisibleElements?: boolean;
  onSectionDeleteClick?: OnSectionDeleteClick;
}

export type SweepCanvasProps<GroupMetadata = any> = BaseSweepCanvasProps<GroupMetadata> &
  SweepCanvasBackgroundProps &
  SweepCanvasControlsProps;

const defaultSweepCanvasProps: SweepCanvasProps = {
  sweepNodes: [],
  sweepEdges: [],
  sweepGroups: [],
  sweepSections: [],
  sweepStandaloneNodes: [],
  sweepStickyNotesNodes: [],
  sweepTextNodes: [],
  visibilityMap: DEFAULT_VISIBILITY_MAP,
};

const SweepCanvasPropsCtx = createContext<SweepCanvasProps>(defaultSweepCanvasProps);

export const useSweepCanvasPropsCtx = () => {
  return useContext(SweepCanvasPropsCtx);
};

export const SweepCanvasPropsCtxProvider = ({
  children,
  value,
}: {
  children: React.ReactNode;
  value: SweepCanvasProps;
}) => {
  return <SweepCanvasPropsCtx.Provider value={value}>{children}</SweepCanvasPropsCtx.Provider>;
};
